import { useDispatch } from 'react-redux';
import { useSelector, RootState } from '../store';
import { useAuth } from './use-auth';
import { roleCan } from '../lib/rbac';
import {
  buildFieldKey,
  buildTabKey,
  ReviewNumber,
} from '../utils/field-key';
import { isMandatoryFieldKey, isMandatoryTabKey } from '../config/field-registry';
import {
  deactivateField,
  reactivateField,
  deactivateTab,
  reactivateTab,
} from '../slices/deactivationSlice';

// Central access point for field/tab deactivation. The <DeactivatableField>
// wrapper and the review tables use this to decide what to hide and whether to
// show the (admin-only) deactivate affordance.
export const useDeactivation = () => {
  const dispatch = useDispatch();
  const { role } = useAuth() as any;
  // Tolerate a store without the deactivation slice (e.g. minimal test mocks);
  // in production the slice is always registered, so this is just hardening.
  const deactivation = useSelector((state: RootState) => state.deactivation);
  const {
    userFields = [],
    userTabs = [],
    companyFields = [],
    companyTabs = [],
  } = deactivation || {};

  // Decision: only project admins may deactivate/reactivate fields.
  const canManage = roleCan(role, 'project:admin');

  const isFieldKeyOff = (key: string): boolean =>
    userFields.includes(key) || companyFields.includes(key);
  const isTabKeyOff = (key: string): boolean =>
    userTabs.includes(key) || companyTabs.includes(key);

  return {
    canManage,
    // Key-based primitives
    isFieldKeyOff,
    isTabKeyOff,
    isFieldKeyMandatory: isMandatoryFieldKey,
    isTabKeyMandatory: isMandatoryTabKey,
    // Convenience builders + checks
    fieldKey: (review: ReviewNumber, screen: string, field: string) =>
      buildFieldKey(review, screen, field),
    tabKey: (review: ReviewNumber, screen: string) => buildTabKey(review, screen),
    isFieldOff: (review: ReviewNumber, screen: string, field: string) =>
      isFieldKeyOff(buildFieldKey(review, screen, field)),
    isTabOff: (review: ReviewNumber, screen: string) =>
      isTabKeyOff(buildTabKey(review, screen)),
    // Mutations (no-ops for non-admins; backend re-enforces)
    deactivateFieldKey: (key: string) => dispatch(deactivateField(key) as any),
    reactivateFieldKey: (key: string) => dispatch(reactivateField(key) as any),
    deactivateTabKey: (key: string) => dispatch(deactivateTab(key) as any),
    reactivateTabKey: (key: string) => dispatch(reactivateTab(key) as any),
  };
};
